import Box from "@mui/material/Box";
import React, { useContext, useEffect, useReducer, useState } from "react";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { CustomerDropdown } from "../components/CustomerDropdown";
import { Customer } from "../components/shared/interfaces/Customer";
import { useAlternativeRegionNames } from "../components/data/Settings/useAlternativeRegionNames";
import { Card } from "../components/shared/Card";
import { AuthContext } from "../components/contexts/AuthContext";
import { ProgressButton } from "../components/shared/ProgressButton";
import { FormContainer, FormItem } from "../components/shared/FormLayout/FormContainer";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { CancelButton } from "../components/shared/CancelButton";
import {
  AlternativeRegionNames,
  AlternativeRegionNamesSubmission,
} from "../components/shared/interfaces/AlternativeRegionNames";
import { useForm } from "react-hook-form";
import { useAlternativeRegionNamesUpdate } from "../components/data/Settings/useAlternativeRegionNamesUpdate";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Col, Container, Row } from "react-bootstrap";
import { InputText } from "primereact/inputtext";
import { ValidationMessage } from "../components/shared/ValidationMessage";
import { MessageToast, ToastMode } from "../components/shared/MessageToast";
import { MessageCard, MessageType } from "../components/shared/MessageCard";
import { Snackbar } from "../components/shared/Snackbar";
import { useDomainMappings } from "../components/data/Settings/useDomainMappings";
import { DomainMappingTable } from "../components/customerSettings/DomainMappingTable";
import { DomainMappingTableEntry } from "../components/customerSettings/DomainMappingTableEntry";
import { v4 as uuid } from "uuid";
import { useDomainMappingsUpdate } from "../components/data/Settings/useDomainMappingsUpdate";
import { DomainMappingPayload } from "../components/shared/interfaces/DomainMappingPayload";
import { CustomerSetupModal } from "../components/customerSettings/CustomerSetupModal";
import { useCustomers } from "../components/data/useCustomers";

export function CustomerSettings() {
  const { t } = useTranslation();

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();

  const retrievedAltRegionNames = useAlternativeRegionNames(selectedCustomer?.id);

  const retrievedDomainMappings = useDomainMappings(selectedCustomer?.id);

  const {
    register,
    formState: { errors },
    clearErrors,
    reset,
    trigger,
    setValue,
  } = useForm<AlternativeRegionNames>({ mode: "onBlur" });

  const [altRegionNames, setAltRegionNames] = useReducer(
    (
      altRegionNames: Partial<AlternativeRegionNames> | undefined,
      updatedAltRegionNames: Partial<AlternativeRegionNames> | undefined,
    ) => {
      return { ...altRegionNames, ...updatedAltRegionNames };
    },
    {},
  );

  const [editingAltRegionNames, setEditingAltRegionNames] = useState(false);

  const [editingDomainMappings, setEditingDomainMappings] = useState(false);

  const [initialDomainsMappings, setInitialDomainsMappings] = useState<DomainMappingTableEntry[]>([]);

  const [editableDomainMappings, setEditableDomainMappings] = useState<DomainMappingTableEntry[]>([]);

  const authContext = useContext(AuthContext);
  const userCanViewAltRegionNames = authContext.canI("view", "customerSettings");
  const userCanEditAltRegionNames = authContext.canI("edit", "customerSettings");

  const userCanEditDomains = authContext.canI("edit", "customerSettings");

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const [showErrorToast, setShowErrorToast] = useState(false);

  const [showInvalidDomainToast, setShowInvalidDomainToast] = useState(false);

  const [showInvalidDomainUpdateToast, setShowInvalidDomainUpdateToast] = useState(false);

  const [invalidDomainUpdateMessage, setInvalidDomainUpdateMessage] = useState("");

  const [customerModalOpen, setShowCustomerModal] = useState(false);

  const canAddCustomer = authContext.canI("add", "customerSettings");

  const customers = useCustomers();

  useEffect(() => {
    if (retrievedAltRegionNames.data) {
      setAltRegionNames(retrievedAltRegionNames.data);
    }
  }, [retrievedAltRegionNames.data]);

  useEffect(() => {
    if (retrievedDomainMappings.data) {
      setEditableDomainMappings(
        retrievedDomainMappings.data.map((domainMapping) => {
          return {
            rowId: uuid(),
            customerId: domainMapping.customerId,
            domain: domainMapping.domain,
            customerName: domainMapping.customerName,
          };
        }),
      );
    }
  }, [retrievedDomainMappings.data]);

  function handleCustomerSelect(customer?: Customer) {
    setEditingDomainMappings(false);
    setEditableDomainMappings([]);
    setSelectedCustomer(customer);
  }

  function onUpdateAltRegionNamesSuccess() {
    setSnackbarText(t("common.dataUpdated", { val: "$t(settings.alternativeSubsetNames)" }));
    setShowSnackbar(true);
    setEditingAltRegionNames(false);
    retrievedAltRegionNames.refetch();
  }

  function onUpdateDomainMappingSuccess() {
    setSnackbarText(t("common.dataUpdated", { val: "$t(settings.domainMappings)" }));
    setShowSnackbar(true);
    setEditingDomainMappings(false);
    setInitialDomainsMappings(editableDomainMappings);
    retrievedDomainMappings.refetch();
  }

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const fieldName = event.target.name as keyof AlternativeRegionNames;
    const value = event.target.value;
    clearErrors(fieldName);
    setValue(fieldName, value, { shouldValidate: false });
    setAltRegionNames({ ...altRegionNames, [fieldName]: value });
  };

  function handleSaveUpdates() {
    trigger().then((isValid) => {
      if (altRegionNames && userCanEditAltRegionNames && isValid) {
        const altRegionNameDetails: Partial<AlternativeRegionNames> = {
          companyAltName: altRegionNames.companyAltName,
          companiesAltName: altRegionNames.companiesAltName,
          siteAltName: altRegionNames.siteAltName,
          sitesAltName: altRegionNames.sitesAltName,
        };

        const altRegionNamesUpdateSubmission: AlternativeRegionNamesSubmission = {
          customerId: selectedCustomer?.id,
          altRegionNames: altRegionNameDetails,
        };

        updateAltRegionNames.mutate(altRegionNamesUpdateSubmission);
      }
    });
  }

  const updateAltRegionNames = useAlternativeRegionNamesUpdate({
    onSuccess: () => onUpdateAltRegionNamesSuccess(),
    onError: () => setShowErrorToast(true),
  });

  const updateDomainMappings = useDomainMappingsUpdate({
    onSuccess: () => onUpdateDomainMappingSuccess(),
    onError: async (error) => {
      await handleShowInvalidDomainUpdateToast(error);
    },
  });

  async function handleShowInvalidDomainUpdateToast(error: Response) {
    if (error.status === 409) {
      const domains = await error.json();
      setInvalidDomainUpdateMessage(
        t("settings.duplicateDomains", {
          domains: domains.duplicates.join(", "),
        }),
      );
    } else {
      setInvalidDomainUpdateMessage(t("settings.unableToUpdateDomain"));
    }
    setShowInvalidDomainUpdateToast(true);
  }

  function handleCancelUpdate() {
    setEditingAltRegionNames(false);
    // Reset form
    reset();
    // Undo any changes that the user has not saved
    setAltRegionNames(retrievedAltRegionNames.data);
  }

  function handleCancelDomainMappingUpdate() {
    setEditingDomainMappings(false);
    // Undo any changes that the user has not saved
    setEditableDomainMappings(initialDomainsMappings);
  }

  const handleAddDomainMapping = () => {
    setEditableDomainMappings([
      ...editableDomainMappings,
      {
        rowId: uuid(),
        customerId: selectedCustomer?.id,
        domain: "",
        customerName: selectedCustomer?.name ?? "",
      },
    ]);
  };

  const handleUpdateDomainMapping = (domainMappingTableEntry: DomainMappingTableEntry[]) => {
    setEditableDomainMappings([...domainMappingTableEntry]);
  };

  const handleDomainMappingEditClicked = () => {
    setEditingDomainMappings(true);
    setInitialDomainsMappings(editableDomainMappings);
  };

  function handleOpenCustomerModal() {
    setShowCustomerModal(true);
  }

  function handleCloseCustomerModal() {
    setShowCustomerModal(false);
  }

  function handleSaveDomainMappingUpdates() {
    const isValid = checkDomainsAreValid(editableDomainMappings);
    if (editableDomainMappings && userCanEditDomains && selectedCustomer?.id != null) {
      if (isValid) {
        let domainMappingArray: DomainMappingPayload = {
          customerId: selectedCustomer.id,
          domainArray: [],
        };

        domainMappingArray.domainArray = editableDomainMappings
          .filter((domainMapping) => domainMapping && domainMapping.customerId != null && domainMapping.domain != null)
          .map((domainMapping) => {
            return {
              domain: domainMapping.domain,
            };
          });

        updateDomainMappings.mutate(domainMappingArray);
      } else {
        setShowInvalidDomainToast(true);
      }
    }
  }

  const checkDomainsAreValid = (domainMappings: DomainMappingTableEntry[]) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}(\.[a-zA-Z0-9][a-zA-Z0-9-]{1,61})*\.[a-zA-Z]{2,}$/;
    return domainMappings.every((domainMapping) => {
      return domainMapping.domain != null && domainMapping.domain !== "" && domainRegex.test(domainMapping.domain);
    });
  };

  const altRegionNamesList: FormItem[] = [
    {
      label: t("common.company"),
      required: editingAltRegionNames && true,
      value: editingAltRegionNames ? (
        <div>
          <div>
            <InputText
              value={altRegionNames.companyAltName || ""}
              maxLength={50}
              className={errors.companyAltName ? "p-invalid" : undefined}
              id={"companyAltName"}
              {...register("companyAltName", {
                required: t<string>("common.pleaseEnter", {
                  val: t("settings.companyName", {
                    val: altRegionNames.companyAltName ?? "$t(common.company)",
                  }),
                }),
                value: altRegionNames.companyAltName,
              })}
              onChange={handleInputChange}
              name={"companyAltName"}
            />
          </div>
          <div>
            {errors?.companyAltName?.message && (
              <ValidationMessage invalidMessage={errors?.companyAltName?.message} validationType="error" showIcon />
            )}
          </div>
        </div>
      ) : (
        altRegionNames?.companyAltName
      ),
    },
    {
      label: t("common.companies"),
      required: editingAltRegionNames && true,
      value: editingAltRegionNames ? (
        <div>
          <div>
            <InputText
              value={altRegionNames.companiesAltName || ""}
              maxLength={50}
              className={errors.companiesAltName ? "p-invalid" : undefined}
              id={"companiesAltName"}
              {...register("companiesAltName", {
                required: t<string>("common.pleaseEnter", {
                  val: t("settings.companyPluralName", {
                    val: altRegionNames.companiesAltName ?? "$t(common.companies)",
                  }),
                }),
                value: altRegionNames.companiesAltName,
              })}
              onChange={handleInputChange}
              name={"companiesAltName"}
            />
          </div>
          <div>
            {errors?.companiesAltName?.message && (
              <ValidationMessage invalidMessage={errors?.companiesAltName?.message} validationType="error" showIcon />
            )}
          </div>
        </div>
      ) : (
        altRegionNames?.companiesAltName
      ),
    },
    {
      label: t("common.site"),
      required: editingAltRegionNames && true,
      value: editingAltRegionNames ? (
        <div>
          <div>
            <InputText
              value={altRegionNames.siteAltName || ""}
              maxLength={50}
              className={errors.siteAltName ? "p-invalid" : undefined}
              id={"siteAltName"}
              {...register("siteAltName", {
                required: t<string>("common.pleaseEnter", {
                  val: t("settings.siteName", {
                    val: altRegionNames.siteAltName ?? "$t(common.site)",
                  }),
                }),
                value: altRegionNames.siteAltName,
              })}
              onChange={handleInputChange}
              name={"siteAltName"}
            />
          </div>
          <div>
            {errors?.siteAltName?.message && (
              <ValidationMessage invalidMessage={errors?.siteAltName?.message} validationType="error" showIcon />
            )}
          </div>
        </div>
      ) : (
        altRegionNames?.siteAltName
      ),
    },
    {
      label: t("common.sites"),
      required: editingAltRegionNames && true,
      value: editingAltRegionNames ? (
        <div>
          <div>
            <InputText
              value={altRegionNames.sitesAltName || ""}
              maxLength={50}
              className={errors.sitesAltName ? "p-invalid" : undefined}
              id={"sitesAltName"}
              {...register("sitesAltName", {
                required: t<string>("common.pleaseEnter", {
                  val: t("settings.sitePluralName", {
                    val: altRegionNames.sitesAltName ?? "$t(common.sites)",
                  }),
                }),
                value: altRegionNames.sitesAltName,
              })}
              onChange={handleInputChange}
              name={"sitesAltName"}
            />
          </div>
          <div>
            {errors?.sitesAltName?.message && (
              <ValidationMessage invalidMessage={errors?.sitesAltName?.message} validationType="error" showIcon />
            )}
          </div>
        </div>
      ) : (
        altRegionNames?.sitesAltName
      ),
    },
  ];

  if (retrievedAltRegionNames.error) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageToast
          text={t("common.dataFetchError", {
            val: "$t(settings.customerSettings)",
          })}
          mode={ToastMode.Error}
          show
        />
      </div>
    );
  } else if (userCanViewAltRegionNames === false) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        <MessageCard
          messageType={MessageType.Warning}
          title={t("common.operationForbiddenError", {
            operation: t("common.view"),
            item: t("settings.customerSettings"),
          })}
          secondaryText={t("common.requestAccess")}
        />
      </div>
    );
  } else {
    return (
      <React.Fragment>
        <Container fluid>
          <Row>
            <Col className="pb-3">
              <Box>
                <RouteBreadcrumbs />
              </Box>
            </Col>
          </Row>
          <Row>
            <Col className="pb-3">
              <div className="d-flex align-items-end gap-5">
                <CustomerDropdown
                  selectedCustomer={selectedCustomer}
                  onCustomerSelect={handleCustomerSelect}
                  onSingleCustomerAvailable={(customer) => setSelectedCustomer(customer)}
                  showLabel
                />
                {canAddCustomer && (
                  <Button
                    sx={{ marginLeft: "8px", height: "40px" }}
                    variant="contained"
                    color="primary"
                    onClick={handleOpenCustomerModal}
                  >
                    {t("settings.addNewCustomer")}
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <Card
                title={t("settings.alternativeSubsetNames")}
                className="mb-3"
                loading={retrievedAltRegionNames.isLoading}
                action={
                  userCanEditAltRegionNames && (
                    <>
                      <Box display="flex" justifyContent="flex-end">
                        {editingAltRegionNames ? (
                          <>
                            <ProgressButton
                              startIcon={<SaveIcon />}
                              onClick={handleSaveUpdates}
                              disabled={updateAltRegionNames.isLoading}
                              loading={updateAltRegionNames.isLoading}
                            >
                              {" "}
                              {t("common.save")}{" "}
                            </ProgressButton>
                            <CancelButton onClick={handleCancelUpdate} className="m1-1" />
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={() => setEditingAltRegionNames(true)}
                          >
                            {t("common.edit")}{" "}
                          </Button>
                        )}
                      </Box>
                    </>
                  )
                }
              >
                {<FormContainer dataList={altRegionNamesList} />}
              </Card>
              <div>
                {showErrorToast && (
                  <MessageToast
                    text={t("common.dataUpdateError", {
                      val: "$t(settings.alternativeSubsetNames)",
                    })}
                    mode={ToastMode.Error}
                    show={showErrorToast}
                    autoHide
                  ></MessageToast>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6}>
              <Card
                title={t("settings.domainMappings")}
                className="mb-3"
                loading={retrievedDomainMappings.isLoading}
                action={
                  userCanEditDomains && (
                    <>
                      <Box display="flex" justifyContent="flex-end">
                        {editingDomainMappings ? (
                          <>
                            <ProgressButton
                              startIcon={<SaveIcon />}
                              onClick={handleSaveDomainMappingUpdates}
                              disabled={updateDomainMappings.isLoading}
                              loading={updateDomainMappings.isLoading}
                            >
                              {" "}
                              {t("common.save")}{" "}
                            </ProgressButton>
                            <CancelButton onClick={handleCancelDomainMappingUpdate} className="m1-1" />
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<EditIcon />}
                            onClick={handleDomainMappingEditClicked}
                          >
                            {t("common.edit")}{" "}
                          </Button>
                        )}
                      </Box>
                    </>
                  )
                }
              >
                <DomainMappingTable
                  domainMappingTableEntries={editableDomainMappings}
                  onUpdatedDomainMappingTableEntries={handleUpdateDomainMapping}
                  onAddDomainClick={handleAddDomainMapping}
                  loading={retrievedDomainMappings.isLoading}
                  isEdit={editingDomainMappings}
                />
              </Card>
              <div>
                {showInvalidDomainToast && (
                  <MessageToast
                    text={t("settings.invalidDomainFormat")}
                    mode={ToastMode.Error}
                    show={showInvalidDomainToast}
                    autoHide
                    onAutohide={() => setShowInvalidDomainToast(false)}
                  />
                )}
                {showInvalidDomainUpdateToast && (
                  <MessageToast
                    text={invalidDomainUpdateMessage}
                    mode={ToastMode.Error}
                    show={showInvalidDomainUpdateToast}
                    autoHide
                    onAutohide={() => setShowInvalidDomainUpdateToast(false)}
                  />
                )}
              </div>
            </Col>
          </Row>
        </Container>

        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />

        <CustomerSetupModal
          isOpen={customerModalOpen}
          onClose={handleCloseCustomerModal}
          onCustomerUpdated={() => {
            customers.refetch();
            handleCloseCustomerModal();
          }}
        />
      </React.Fragment>
    );
  }
}
